import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Container, Table, Alert, Spinner, Button, Stack, Row, Col, Badge, Form, Modal, ListGroup, Card, ProgressBar } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { db } from '../../../firebase';
import { collection, getDocs, query, orderBy, doc, deleteDoc, where, getDoc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../../components/context/AuthContext';
import { useNotification } from '../../../components/ui/Notification';
import BreadcrumbNavigation from '../../../components/ui/BreadcrumbNavigation';
import Pagination from 'react-bootstrap/Pagination';
import { createLogger } from '../../../utils/logger';
import { parseInventoryCsv, buildInventoryCsvPreview, buildProductsByUpc, readCsvFileAsText } from '../../../utils/parseInventoryCsv';
import { executeBatchOperations, createInventoryUpdateOperations, createInventoryAdditionOperations } from '../../../utils/firestoreBatch';

const logger = createLogger('InventoryPage');

const ITEMS_PER_PAGE = 10;
const LOW_STOCK_THRESHOLD = 10;
const MAX_CSV_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_CSV_ROWS = 5000;

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set([1, totalPages]);
  for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
    if (page >= 1 && page <= totalPages) {
      pages.add(page);
    }
  }

  const sortedPages = [...pages].sort((a, b) => a - b);
  const items = [];

  sortedPages.forEach((page, index) => {
    if (index > 0 && page - sortedPages[index - 1] > 1) {
      items.push('ellipsis');
    }
    items.push(page);
  });

  return items;
};

const getCsvPreviewUpdateKey = (item) => `update:${item.id}`;

const getCsvPreviewAdditionKey = (item) => `add:${item.supermarketId}-${item.productId}-${item.productUpc}`;

const getAllCsvPreviewRowKeys = (previewData) => {
  const keys = [];
  previewData?.updates?.forEach((item) => keys.push(getCsvPreviewUpdateKey(item)));
  previewData?.additions?.forEach((item) => keys.push(getCsvPreviewAdditionKey(item)));
  return keys;
};

const InventoryPage = () => {
  const { userRole, employeeSupermarketId, currentUser } = useAuth();
  const { showNotification } = useNotification();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Discount deals modal state
  const [showDealsModal, setShowDealsModal] = useState(false);
  const [selectedItemDeals, setSelectedItemDeals] = useState([]);
  const [selectedItemName, setSelectedItemName] = useState('');

  const [csvFile, setCsvFile] = useState(null);
  const [csvFileText, setCsvFileText] = useState('');
  const [csvFileReading, setCsvFileReading] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvUploadProgress, setCsvUploadProgress] = useState({ current: 0, total: 0 });
  const [csvSupermarketId, setCsvSupermarketId] = useState('');
  const [supermarkets, setSupermarkets] = useState([]);
  const [showCsvResultsModal, setShowCsvResultsModal] = useState(false);
  const [showCsvPreviewModal, setShowCsvPreviewModal] = useState(false);
  const [csvPreviewData, setCsvPreviewData] = useState(null);
  const [csvPreviewSelectedKeys, setCsvPreviewSelectedKeys] = useState(() => new Set());
  const [csvUploadResults, setCsvUploadResults] = useState(null);
  const csvInputRef = useRef(null);
  
  const navigate = useNavigate();

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    userRole === 'Admin' ? { path: "/admin", label: "Admin Panel" } : { path: "/employee/dashboard", label: "Employee Hub" },
    { label: "Inventory Management" }
  ];

  // Handle viewing discount deals
  const handleViewDeals = (item) => {
    const deals = item.discount_deals || [];
    setSelectedItemDeals(deals);
    setSelectedItemName(item.product_name || 'Unknown Product');
    setShowDealsModal(true);
  };

  const handleCloseDealsModal = () => {
    setShowDealsModal(false);
    setSelectedItemDeals([]);
    setSelectedItemName('');
  };

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let inventoryQuery;
      if (userRole === 'Admin') {
        inventoryQuery = query(collection(db, 'Inventory'), orderBy('created_at', 'desc'));
      } else if (userRole === 'Employee' && employeeSupermarketId) {
        inventoryQuery = query(
          collection(db, 'Inventory'), 
          where('supermarket_id', '==', employeeSupermarketId),
          orderBy('created_at', 'desc')
        );
      } else if (userRole === 'Employee' && !employeeSupermarketId) {
        setInventory([]); setLoading(false); return;
      } else {
        setError("You are not authorized to view inventory."); setInventory([]); setLoading(false); return;
      }
      const inventorySnapshot = await getDocs(inventoryQuery);
      let inventoryList = inventorySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      const productIdsNeedingUpc = [...new Set(
        inventoryList
          .filter((item) => !item.product_upc && item.product_id)
          .map((item) => item.product_id)
      )];
      if (productIdsNeedingUpc.length > 0) {
        const upcByProductId = {};
        await Promise.all(productIdsNeedingUpc.map(async (productId) => {
          try {
            const productSnap = await getDoc(doc(db, 'Products', productId));
            if (productSnap.exists()) {
              upcByProductId[productId] = productSnap.data().upc || null;
            }
          } catch (upcErr) {
            logger.warn('[InventoryPage] Failed to fetch product UPC:', productId, upcErr);
          }
        }));
        inventoryList = inventoryList.map((item) => ({
          ...item,
          product_upc: item.product_upc || upcByProductId[item.product_id] || null,
        }));
      }

      setInventory(inventoryList);
    } catch (err) {
      setError(`Failed to fetch inventory. ${  err.message}`);
      showNotification({
        type: 'error',
        title: 'Error',
        message: `Failed to fetch inventory. ${err.message}`
      });
    } finally {
      setLoading(false);
    }
  }, [userRole, employeeSupermarketId, showNotification]);

  useEffect(() => {
    if(currentUser && userRole){ // Ensure userRole is available before fetching
        fetchInventory();
    }
  }, [currentUser, userRole, fetchInventory]); // Added userRole to dependencies

  useEffect(() => {
    if (userRole !== 'Admin') {
      setSupermarkets([]);
      setCsvSupermarketId('');
      return;
    }

    const fetchSupermarkets = async () => {
      try {
        const supermarketsSnapshot = await getDocs(collection(db, 'Supermarkets'));
        const supermarketsList = supermarketsSnapshot.docs
          .map((supermarketDoc) => ({
            id: supermarketDoc.id,
            ...supermarketDoc.data(),
          }))
          .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''));
        setSupermarkets(supermarketsList);
      } catch (err) {
        logger.error('[InventoryPage] Failed to fetch supermarkets:', err);
      }
    };

    fetchSupermarkets();
  }, [userRole]);

  const handleGoBack = () => navigate(-1);
  const handleEdit = (itemId) => navigate(`/inventory/edit/${itemId}`);

  const handleDelete = async (itemId, itemSupermarketId) => {
    // Validate input parameters
    if (!itemId || typeof itemId !== 'string' || itemId.trim() === '') {
      showNotification({
        type: 'error',
        title: 'Invalid Input',
        message: 'Invalid inventory item ID.'
      });
      return;
    }

    if (userRole === 'Employee' && itemSupermarketId !== employeeSupermarketId) {
        showNotification({
          type: 'warning',
          title: 'Access Denied',
          message: 'You can only delete inventory items for your assigned supermarket.'
        });
        return;
    }
    
    // Enhanced confirmation dialog
    const itemToDelete = inventory.find(item => item.id === itemId);
    const itemName = itemToDelete ? (itemToDelete.product_name || 'Unknown Product') : 'this item';
    const supermarketName = itemToDelete ? (itemToDelete.supermarket_name || 'Unknown Supermarket') : 'Unknown Supermarket';
    
    const confirmMessage = `Are you sure you want to delete "${itemName}" from ${supermarketName}?\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      // First check if the document exists before trying to delete it
      const docRef = doc(db, 'Inventory', itemId);
      
      // Attempt to delete the document
      await deleteDoc(docRef);
      
      // Update local state only after successful deletion
      setInventory((prevInventory) => prevInventory.filter((item) => item.id !== itemId));
      
      // Show success notification
      showNotification({
        type: 'success',
        title: 'Success',
        message: `Successfully deleted "${itemName}" from inventory.`
      });
      
    } catch (err) {
      logger.error('Delete error:', err);
      
      // Provide more specific error messages based on the error type
      let errorMessage = `Failed to delete item ${itemId}. Error: ${err.message}`;
      if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. You don\'t have access to delete this item.';
      } else if (err.code === 'not-found') {
        errorMessage = 'Item not found. It may have already been deleted.';
      } else if (err.code === 'unavailable') {
        errorMessage = 'Service temporarily unavailable. Please try again.';
      }
      
      showNotification({
        type: 'error',
        title: 'Delete Failed',
        message: errorMessage
      });
    }
  };

  const canAddInventory = userRole === 'Admin' || (userRole === 'Employee' && employeeSupermarketId);

  const csvInventoryInScope = useMemo(() => {
    if (userRole === 'Employee' && employeeSupermarketId) {
      return inventory.filter((item) => item.supermarket_id === employeeSupermarketId);
    }
    if (userRole === 'Admin' && csvSupermarketId) {
      return inventory.filter((item) => item.supermarket_id === csvSupermarketId);
    }
    return inventory;
  }, [inventory, userRole, employeeSupermarketId, csvSupermarketId]);

  const csvScopeLabel = useMemo(() => {
    if (userRole === 'Employee') {
      return 'your assigned supermarket';
    }
    if (csvSupermarketId) {
      const selected = supermarkets.find((supermarket) => supermarket.id === csvSupermarketId);
      return selected?.display_name || 'the selected supermarket';
    }
    return 'all supermarkets';
  }, [userRole, csvSupermarketId, supermarkets]);

  const csvTargetSupermarkets = useMemo(() => {
    if (userRole === 'Employee' && employeeSupermarketId) {
      const supermarketName = inventory.find((item) => item.supermarket_id === employeeSupermarketId)?.supermarket_name;
      return [{
        id: employeeSupermarketId,
        display_name: supermarketName || 'Assigned supermarket',
      }];
    }
    if (userRole === 'Admin' && csvSupermarketId) {
      const selected = supermarkets.find((supermarket) => supermarket.id === csvSupermarketId);
      return [{
        id: csvSupermarketId,
        display_name: selected?.display_name || csvSupermarketId,
      }];
    }
    return [];
  }, [userRole, employeeSupermarketId, csvSupermarketId, supermarkets, inventory]);

  const handleCsvFileChange = async (event) => {
    const file = event.target.files?.[0] || null;
    setCsvFile(file);
    setCsvFileText('');

    if (!file) {
      return;
    }

    if (file.size > MAX_CSV_FILE_SIZE) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      showNotification({
        type: 'error',
        title: 'File too large',
        message: `CSV file must be under 5MB. Selected file is ${fileSizeMB}MB.`,
      });
      resetCsvInput();
      return;
    }

    setCsvFileReading(true);
    try {
      const text = await readCsvFileAsText(file);
      setCsvFileText(text);
    } catch (readErr) {
      logger.error('[InventoryPage] Failed to read CSV file on select:', readErr);
      showNotification({
        type: 'error',
        title: 'File read failed',
        message: 'Could not read the selected CSV file. Please choose the file again.',
      });
      resetCsvInput();
    } finally {
      setCsvFileReading(false);
    }
  };

  const resetCsvInput = () => {
    setCsvFile(null);
    setCsvFileText('');
    if (csvInputRef.current) {
      csvInputRef.current.value = '';
    }
  };

  const resolveCsvFileText = async () => {
    if (csvFileText) {
      return csvFileText;
    }

    const file = csvInputRef.current?.files?.[0] || csvFile;
    if (!file) {
      return null;
    }

    const text = await readCsvFileAsText(file);
    setCsvFileText(text);
    return text;
  };

  const handleCsvPreview = async (event) => {
    event.preventDefault();

    if (!csvFile && !csvInputRef.current?.files?.[0]) {
      showNotification({
        type: 'warning',
        title: 'No file selected',
        message: 'Please choose a CSV file to upload.',
      });
      return;
    }

    if (csvFileReading) {
      showNotification({
        type: 'warning',
        title: 'File still loading',
        message: 'Please wait for the CSV file to finish loading.',
      });
      return;
    }

    if (!currentUser?.uid) {
      showNotification({
        type: 'error',
        title: 'Authentication required',
        message: 'You must be logged in to upload inventory updates.',
      });
      return;
    }

    let fileText;
    try {
      fileText = await resolveCsvFileText();
    } catch (readErr) {
      logger.error('[InventoryPage] CSV file read failed:', readErr);
      showNotification({
        type: 'error',
        title: 'Preview failed',
        message: 'Could not read the CSV file. Please re-select the file and try again.',
      });
      return;
    }

    if (!fileText?.trim()) {
      showNotification({
        type: 'warning',
        title: 'Empty file',
        message: 'The selected CSV file is empty.',
      });
      return;
    }

    try {
      const { rows, errors: parseErrors } = parseInventoryCsv(fileText);

      if (parseErrors.length > 0 && rows.length === 0) {
        showNotification({
          type: 'error',
          title: 'Invalid CSV',
          message: parseErrors[0],
        });
        return;
      }

      if (rows.length === 0) {
        showNotification({
          type: 'warning',
          title: 'Empty CSV',
          message: 'No valid rows were found in the CSV file.',
        });
        return;
      }

      if (rows.length > MAX_CSV_ROWS) {
        showNotification({
          type: 'error',
          title: 'Too many rows',
          message: `CSV has ${rows.length.toLocaleString()} rows, which exceeds the limit of ${MAX_CSV_ROWS.toLocaleString()} rows per upload. Please split into smaller files.`,
        });
        return;
      }

      const [productsSnapshot] = await Promise.all([
        getDocs(collection(db, 'Products')),
      ]);
      const products = productsSnapshot.docs.map((productDoc) => ({
        id: productDoc.id,
        ...productDoc.data(),
      }));
      const productsByUpc = buildProductsByUpc(products);
      const preview = buildInventoryCsvPreview(rows, csvInventoryInScope, {
        productsByUpc,
        targetSupermarkets: csvTargetSupermarkets,
      });

      setCsvPreviewData({
        ...preview,
        parseErrors,
        scopeLabel: csvScopeLabel,
      });
      setCsvPreviewSelectedKeys(new Set(getAllCsvPreviewRowKeys(preview)));
      setShowCsvPreviewModal(true);
    } catch (err) {
      logger.error('[InventoryPage] CSV preview failed:', err);
      showNotification({
        type: 'error',
        title: 'Preview failed',
        message: `Failed to process CSV file. ${err.message}`,
      });
    }
  };

  const handleCloseCsvPreviewModal = () => {
    setShowCsvPreviewModal(false);
    setCsvPreviewData(null);
    setCsvPreviewSelectedKeys(new Set());
  };

  const toggleCsvPreviewRow = (rowKey) => {
    setCsvPreviewSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  };

  const csvPreviewSelectableKeys = useMemo(
    () => (csvPreviewData ? getAllCsvPreviewRowKeys(csvPreviewData) : []),
    [csvPreviewData]
  );

  const allCsvPreviewRowsSelected = useMemo(
    () => csvPreviewSelectableKeys.length > 0
      && csvPreviewSelectableKeys.every((key) => csvPreviewSelectedKeys.has(key)),
    [csvPreviewSelectableKeys, csvPreviewSelectedKeys]
  );

  const selectedCsvPreviewUpdateCount = useMemo(
    () => (csvPreviewData?.updates || []).filter((item) => csvPreviewSelectedKeys.has(getCsvPreviewUpdateKey(item))).length,
    [csvPreviewData, csvPreviewSelectedKeys]
  );

  const selectedCsvPreviewAdditionCount = useMemo(
    () => (csvPreviewData?.additions || []).filter((item) => csvPreviewSelectedKeys.has(getCsvPreviewAdditionKey(item))).length,
    [csvPreviewData, csvPreviewSelectedKeys]
  );

  const handleToggleAllCsvPreviewRows = () => {
    if (allCsvPreviewRowsSelected) {
      setCsvPreviewSelectedKeys(new Set());
      return;
    }
    setCsvPreviewSelectedKeys(new Set(csvPreviewSelectableKeys));
  };

  const handleCsvApplyConfirmed = async () => {
    const updates = (csvPreviewData?.updates || []).filter((item) =>
      csvPreviewSelectedKeys.has(getCsvPreviewUpdateKey(item))
    );
    const additions = (csvPreviewData?.additions || []).filter((item) =>
      csvPreviewSelectedKeys.has(getCsvPreviewAdditionKey(item))
    );

    if ((!updates.length && !additions.length) || !currentUser?.uid) {
      return;
    }

    const { notFoundUpcs, parseErrors, scopeLabel, pendingSupermarketUpcs } = csvPreviewData;
    const totalOperations = updates.length + additions.length;

    setCsvUploading(true);
    setCsvUploadProgress({ current: 0, total: totalOperations });

    try {
      const userInfo = {
        uid: currentUser.uid,
        email: currentUser.email || 'Unknown User',
      };

      let updateResults = { success: [], failed: [] };
      let additionResults = { success: [], failed: [] };

      if (updates.length > 0) {
        logger.info(`[InventoryPage] Processing ${updates.length} inventory updates using batch operations`);
        const updateOperations = createInventoryUpdateOperations(updates, userInfo);
        updateResults = await executeBatchOperations(updateOperations, db);
        setCsvUploadProgress({ current: updateResults.success.length + updateResults.failed.length, total: totalOperations });
      }

      if (additions.length > 0) {
        logger.info(`[InventoryPage] Processing ${additions.length} inventory additions using batch operations`);
        const additionOperations = createInventoryAdditionOperations(additions, db, userInfo);
        additionResults = await executeBatchOperations(additionOperations, db);
        setCsvUploadProgress({ 
          current: updateResults.success.length + updateResults.failed.length + additionResults.success.length + additionResults.failed.length, 
          total: totalOperations 
        });
      }

      await fetchInventory();

      const updatedItems = updateResults.success.map((result) => {
        const originalUpdate = updates.find((u) => u.upc === result.metadata.upc);
        return { ...originalUpdate, id: result.operation.id };
      });

      const addedItems = additionResults.success.map((result) => {
        const originalAddition = additions.find((a) => a.productUpc === result.metadata.upc);
        return { ...originalAddition, id: result.metadata.newDocId };
      });

      const failedUpdates = [
        ...updateResults.failed.map((failure) => ({
          upc: failure.metadata.upc,
          productName: failure.metadata.productName,
          message: failure.error,
          action: failure.metadata.action,
        })),
        ...additionResults.failed.map((failure) => ({
          upc: failure.metadata.upc,
          productName: failure.metadata.productName,
          message: failure.error,
          action: failure.metadata.action,
        })),
      ];

      setCsvUploadResults({
        updatedCount: updatedItems.length,
        addedCount: addedItems.length,
        notFoundUpcs,
        pendingSupermarketUpcs,
        parseErrors,
        failedUpdates,
        updatedItems,
        addedItems,
        scopeLabel,
      });
      setShowCsvResultsModal(true);
      handleCloseCsvPreviewModal();

      const summaryParts = [];
      if (updatedItems.length > 0) {
        summaryParts.push(`updated ${updatedItems.length} item${updatedItems.length !== 1 ? 's' : ''}`);
      }
      if (addedItems.length > 0) {
        summaryParts.push(`added ${addedItems.length} item${addedItems.length !== 1 ? 's' : ''}`);
      }

      showNotification({
        type: failedUpdates.length > 0 ? 'warning' : 'success',
        title: failedUpdates.length > 0 ? 'Partial update complete' : 'Update complete',
        message: `Successfully ${summaryParts.join(' and ')}.${notFoundUpcs.length > 0 ? ` ${notFoundUpcs.length} UPC${notFoundUpcs.length !== 1 ? 's' : ''} were not found in products.` : ''}`,
      });

      resetCsvInput();
    } catch (err) {
      logger.error('[InventoryPage] CSV apply failed:', err);
      showNotification({
        type: 'error',
        title: 'Update failed',
        message: `Failed to apply CSV updates. ${err.message}`,
      });
    } finally {
      setCsvUploading(false);
      setCsvUploadProgress({ current: 0, total: 0 });
    }
  };

  const handleCloseCsvResultsModal = () => {
    setShowCsvResultsModal(false);
    setCsvUploadResults(null);
  };

  // Filter inventory based on search term
  const filteredInventory = useMemo(() => {
    if (!searchTerm.trim()) return inventory;

    const rawTerm = searchTerm.trim();
    const searchLower = rawTerm.toLowerCase();
    return inventory.filter(item => 
      (item.product_name && item.product_name.toLowerCase().includes(searchLower)) ||
      (item.product_upc && item.product_upc.includes(rawTerm)) ||
      (item.supermarket_name && item.supermarket_name.toLowerCase().includes(searchLower)) ||
      (item.created_by_email && item.created_by_email.toLowerCase().includes(searchLower)) ||
      (item.sale_price && item.sale_price.toString().includes(searchLower)) ||
      (item.quantity && item.quantity.toString().includes(searchLower))
    );
  }, [inventory, searchTerm]);

  // Dashboard summary stats (from full inventory, not search-filtered)
  const summaryStats = useMemo(() => {
    let totalQuantity = 0;
    let lowStock = 0;
    let outOfStock = 0;
    let itemsWithDeals = 0;
    let totalValue = 0;
    for (const item of inventory) {
      const q = typeof item.quantity === 'number' ? item.quantity : 0;
      const price = typeof item.sale_price === 'number' ? item.sale_price : 0;
      totalQuantity += q;
      if (q === 0) outOfStock++;
      else if (q > 0 && q <= LOW_STOCK_THRESHOLD) lowStock++;
      if (item.discount_deals && item.discount_deals.length > 0) itemsWithDeals++;
      totalValue += q * price;
    }
    return {
      totalItems: inventory.length,
      totalQuantity,
      lowStock,
      outOfStock,
      itemsWithDeals,
      totalValue
    };
  }, [inventory]);

  // Pagination logic
  const totalPages = Math.ceil(filteredInventory.length / ITEMS_PER_PAGE);
  const paginatedInventory = filteredInventory.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (page) => setCurrentPage(page);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    // This component is embedded, so no <Layout> here.
    // The parent (InventoryPublicPageWrapper) provides the Layout.
    <Container className="pt-3">
        <Row className="align-items-center mb-1">
            <Col xs="auto">
                <Button variant="outline-secondary" onClick={handleGoBack} className="mb-2">
                    &larr; Go Back
                </Button>
            </Col>
            <Col>
                <BreadcrumbNavigation items={breadcrumbItems} />
            </Col>
        </Row>
        
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-3">
            <h2 className="mb-0">Inventory Management</h2>
            <div className="d-flex flex-column flex-sm-row gap-2">
                {canAddInventory && (
                    <Link to="/inventory/create" className="btn btn-primary">Add Inventory Item</Link>
                )}
                {(userRole === 'Admin' || userRole === 'Employee') && (
                    <Link to="/admin/products" className="btn btn-outline-primary">
                        View product catalog
                    </Link>
                )}
            </div>
        </div>

        {/* Inventory Dashboard */}
        {!loading && !(userRole === 'Employee' && !employeeSupermarketId) && (
          <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-chart-line me-2" aria-hidden="true" />
                Dashboard Summary
              </h5>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Card className="h-100 border-primary">
                    <Card.Body className="text-center">
                      <div className="text-primary mb-2">
                        <i className="fas fa-boxes fa-2x" aria-hidden="true" />
                      </div>
                      <h3 className="mb-1">{summaryStats.totalItems}</h3>
                      <p className="text-muted mb-0 small">Total Items</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Card className="h-100 border-info">
                    <Card.Body className="text-center">
                      <div className="text-info mb-2">
                        <i className="fas fa-cubes fa-2x" aria-hidden="true" />
                      </div>
                      <h3 className="mb-1">{summaryStats.totalQuantity}</h3>
                      <p className="text-muted mb-0 small">Total Quantity</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Card className="h-100 border-warning">
                    <Card.Body className="text-center">
                      <div className="text-warning mb-2">
                        <i className="fas fa-exclamation-triangle fa-2x" aria-hidden="true" />
                      </div>
                      <h3 className="mb-1">{summaryStats.lowStock}</h3>
                      <p className="text-muted mb-0 small">Low Stock (≤{LOW_STOCK_THRESHOLD})</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Card className="h-100 border-danger">
                    <Card.Body className="text-center">
                      <div className="text-danger mb-2">
                        <i className="fas fa-times-circle fa-2x" aria-hidden="true" />
                      </div>
                      <h3 className="mb-1">{summaryStats.outOfStock}</h3>
                      <p className="text-muted mb-0 small">Out of Stock</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Card className="h-100 border-success">
                    <Card.Body className="text-center">
                      <div className="text-success mb-2">
                        <i className="fas fa-tags fa-2x" aria-hidden="true" />
                      </div>
                      <h3 className="mb-1">{summaryStats.itemsWithDeals}</h3>
                      <p className="text-muted mb-0 small">Items with Deals</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Card className="h-100 border-secondary">
                    <Card.Body className="text-center">
                      <div className="text-secondary mb-2">
                        <i className="fas fa-dollar-sign fa-2x" aria-hidden="true" />
                      </div>
                      <h3 className="mb-1">${summaryStats.totalValue.toFixed(2)}</h3>
                      <p className="text-muted mb-0 small">Total Value</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Search Bar */}
        <Row className="mb-4">
            <Col md={6} lg={4}>
                <div className="position-relative">
                    <Form.Control
                        type="text"
                        placeholder="Search inventory by name, UPC, supermarket..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-100"
                        style={{ fontSize: '16px' }} // Prevents zoom on iOS
                    />
                    {searchTerm && (
                        <Button
                            variant="outline-secondary"
                            onClick={() => setSearchTerm('')}
                            style={{ 
                                fontSize: '16px', 
                                minWidth: '44px',
                                position: 'absolute',
                                right: '0',
                                top: '0',
                                height: '100%',
                                borderTopLeftRadius: '0',
                                borderBottomLeftRadius: '0',
                                zIndex: 10
                            }}
                        >
                            ×
                        </Button>
                    )}
                </div>
                <small className="text-muted">
                    Search by product name, UPC, supermarket, price, quantity, or added by
                </small>
            </Col>
        </Row>

        {canAddInventory && (
          <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <i className="fas fa-file-upload me-2" aria-hidden="true" />
                Bulk Update from CSV
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="small text-muted mb-3">
                Upload a CSV file with columns <strong>upc</strong>, <strong>quantity</strong>, and <strong>price</strong>.
                Matching inventory items will be updated. Products in the catalog but not yet in inventory will be added automatically.
                {userRole === 'Employee' && ' Changes apply only to your assigned supermarket.'}
                {userRole === 'Admin' && ' Choose a supermarket to update or add items. Select All supermarkets to update matches across every store (new products require a specific supermarket).'}
              </p>
              <Form onSubmit={handleCsvPreview}>
                <Row className="align-items-end g-2">
                  {userRole === 'Admin' && (
                    <Col xs={12} md={4} lg={3}>
                      <Form.Group controlId="csvSupermarketSelect">
                        <Form.Label className="small fw-semibold">Apply to supermarket</Form.Label>
                        <Form.Select
                          value={csvSupermarketId}
                          onChange={(e) => setCsvSupermarketId(e.target.value)}
                          disabled={csvUploading}
                        >
                          <option value="">All supermarkets</option>
                          {supermarkets.map((supermarket) => (
                            <option key={supermarket.id} value={supermarket.id}>
                              {supermarket.display_name || supermarket.id}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}
                  <Col xs={12} md={8} lg={6}>
                    <Form.Group controlId="inventoryCsvUpload">
                      <Form.Label className="small fw-semibold">CSV file</Form.Label>
                      <div className="inventory-csv-file-picker">
                        <Form.Control
                          ref={csvInputRef}
                          id="inventoryCsvUpload"
                          type="file"
                          accept=".csv,text/csv"
                          onChange={handleCsvFileChange}
                          disabled={csvUploading || csvFileReading}
                          className="inventory-csv-file-input visually-hidden"
                        />
                        <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch align-items-sm-center">
                          <Button
                            type="button"
                            variant="outline-secondary"
                            className="inventory-csv-choose-btn flex-shrink-0"
                            onClick={() => csvInputRef.current?.click()}
                            disabled={csvUploading || csvFileReading}
                            aria-label="Choose CSV file"
                          >
                            Choose file
                          </Button>
                          <span
                            className="inventory-csv-file-name text-muted small align-self-center text-truncate"
                            title={csvFile?.name || undefined}
                          >
                            {csvFile?.name || 'No file chosen'}
                          </span>
                        </div>
                      </div>
                      {csvFileReading && (
                        <Form.Text className="text-muted">Reading CSV file...</Form.Text>
                      )}
                      {!csvFileReading && csvFile && csvFileText && (
                        <Form.Text className="text-success">CSV file ready to apply.</Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col xs={12} md="auto">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={!csvFileText || csvUploading || csvFileReading}
                    >
                      {csvUploading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                          Processing...
                        </>
                      ) : (
                        'Preview updates'
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        )}

      {error && <Alert variant="danger">{error}</Alert>}

      {userRole === 'Employee' && !employeeSupermarketId && !loading && (
          <Alert variant="warning">You are not assigned to a supermarket. Inventory management is unavailable.</Alert>
      )}

      {loading ? (
        <div className="text-center"><Spinner animation="border" role="status"><span className="visually-hidden">Loading...</span></Spinner></div>
      ) : (
        <>
          <Table bordered hover responsive striped>
            <thead>
              <tr>
                <th>#</th><th>Supermarket</th><th>Product</th><th>UPC</th><th>Sale Price</th><th>Quantity</th><th>Deals</th><th>Added By</th><th>Added At</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedInventory.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center">
                    {searchTerm.trim() 
                      ? `No inventory items found matching "${searchTerm}"`
                      : `No inventory items found ${userRole === 'Employee' && employeeSupermarketId ? `for your assigned supermarket` : userRole === 'Employee' ? '(No supermarket assigned)' : '.' }`
                    }
                  </td>
                </tr>
              ) : (
                paginatedInventory.map((item, idx) => {
                  const canModify = userRole === 'Admin' || (userRole === 'Employee' && item.supermarket_id === employeeSupermarketId);
                  return (
                    <tr key={item.id}>
                      <td>{(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}</td>
                      <td>{item.supermarket_name || item.supermarket_id}</td>
                      <td>{item.product_name || item.product_id}</td>
                      <td>{item.product_upc || '—'}</td>
                      <td>${item.sale_price?.toFixed(2)}</td>
                      <td>{item.quantity}</td>
                      <td>
                        {item.discount_deals && item.discount_deals.length > 0 ? (
                          <Badge bg="success">{item.discount_deals.length} deal{item.discount_deals.length !== 1 ? 's' : ''}</Badge>
                        ) : (
                          <span className="text-muted">None</span>
                        )}
                      </td>
                      <td>{item.created_by_email || item.created_by}</td>
                      <td>{item.created_at?.toDate().toLocaleString()}</td>
                      <td>
                        {canModify ? (
                          <Stack direction="horizontal" gap={2}>
                            <Button variant="warning" size="sm" onClick={() => handleEdit(item.id)}>Edit</Button>
                            <Button 
                              variant="danger" 
                              size="sm" 
                              onClick={() => handleDelete(item.id, item.supermarket_id)}
                              disabled={!item.id || item.id.trim() === ''}
                            >
                              Delete
                            </Button>
                          </Stack>
                        ) : (
                          <Badge bg="secondary">View Only</Badge>
                        )}
                        {(item.discount_deals && item.discount_deals.length > 0) && (
                          <Button 
                            variant="info" 
                            size="sm" 
                            onClick={() => handleViewDeals(item)}
                            className="mt-1"
                          >
                            View Deals ({item.discount_deals.length})
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mb-4 px-2">
              <Pagination
                className="inventory-pagination mb-0 flex-wrap justify-content-center"
                aria-label="Inventory pagination"
              >
                <Pagination.First
                  className="d-none d-sm-block"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                />
                {getPaginationItems(currentPage, totalPages).map((item, index) =>
                  item === 'ellipsis' ? (
                    <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                  ) : (
                    <Pagination.Item
                      key={item}
                      active={currentPage === item}
                      onClick={() => handlePageChange(item)}
                    >
                      {item}
                    </Pagination.Item>
                  )
                )}
                <Pagination.Next
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last
                  className="d-none d-sm-block"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
          
          {/* Search Results Summary */}
          {searchTerm.trim() && (
            <div className="text-center text-muted mb-3">
              <small>
                Showing {filteredInventory.length} of {inventory.length} inventory items
                {filteredInventory.length !== inventory.length && ` matching "${searchTerm}"`}
              </small>
            </div>
          )}
        </>
      )}

             {/* Discount Deals Modal */}
       <Modal show={showDealsModal} onHide={handleCloseDealsModal} size="lg">
         <Modal.Header closeButton>
           <Modal.Title>Discount Deals for {selectedItemName}</Modal.Title>
         </Modal.Header>
         <Modal.Body>
           {selectedItemDeals.length === 0 ? (
             <p>No discount deals currently available for this item.</p>
           ) : (
             <ListGroup>
                               {selectedItemDeals.map((deal, index) => (
                  <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{deal.quantity} items</strong> for <strong>${deal.price.toFixed(2)}</strong>
                      <br />
                      <small className="text-success">
                        Per item: ${(deal.price / deal.quantity).toFixed(2)}
                      </small>
                    </div>
                    <Badge bg="success">Deal</Badge>
                  </ListGroup.Item>
                ))}
             </ListGroup>
           )}
         </Modal.Body>
         <Modal.Footer>
           <Button variant="secondary" onClick={handleCloseDealsModal}>Close</Button>
         </Modal.Footer>
       </Modal>

       <Modal show={showCsvPreviewModal} onHide={handleCloseCsvPreviewModal} size="xl" scrollable>
         <Modal.Header closeButton>
           <Modal.Title>Preview CSV Updates</Modal.Title>
         </Modal.Header>
         <Modal.Body>
           {csvUploading && csvUploadProgress.total > 0 && (
             <div className="mb-4">
               <div className="d-flex justify-content-between align-items-center mb-2">
                 <strong>Processing batch operations...</strong>
                 <span className="text-muted">
                   {csvUploadProgress.current} of {csvUploadProgress.total} items
                 </span>
               </div>
               <ProgressBar 
                 now={(csvUploadProgress.current / csvUploadProgress.total) * 100} 
                 label={`${Math.round((csvUploadProgress.current / csvUploadProgress.total) * 100)}%`}
                 animated
                 striped
               />
             </div>
           )}
           {csvPreviewData && (
             <>
               {(csvPreviewData.updates.length > 0 || csvPreviewData.additions.length > 0) && (
                 <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                   <small className="text-muted">
                     {selectedCsvPreviewUpdateCount + selectedCsvPreviewAdditionCount} of{' '}
                     {csvPreviewData.updates.length + csvPreviewData.additions.length} change
                     {csvPreviewData.updates.length + csvPreviewData.additions.length !== 1 ? 's' : ''} selected
                   </small>
                   <Button variant="outline-secondary" size="sm" onClick={handleToggleAllCsvPreviewRows}>
                     {allCsvPreviewRowsSelected ? 'Uncheck all' : 'Check all'}
                   </Button>
                 </div>
               )}

               <p className="mb-3">
                 {csvPreviewData.updates.length > 0 || csvPreviewData.additions.length > 0 ? (
                   <>
                     {csvPreviewData.updates.length > 0 && (
                       <>
                         <strong>{csvPreviewData.updates.length}</strong> item
                         {csvPreviewData.updates.length !== 1 ? 's' : ''} can be updated
                       </>
                     )}
                     {csvPreviewData.updates.length > 0 && csvPreviewData.additions.length > 0 && ', and '}
                     {csvPreviewData.additions.length > 0 && (
                       <>
                         <strong>{csvPreviewData.additions.length}</strong> item
                         {csvPreviewData.additions.length !== 1 ? 's' : ''} can be added
                       </>
                     )}
                     {' '}in <strong>{csvPreviewData.scopeLabel}</strong> from{' '}
                     <strong>{csvPreviewData.csvRowCount}</strong> CSV row
                     {csvPreviewData.csvRowCount !== 1 ? 's' : ''}. Uncheck any rows you want to skip.
                   </>
                 ) : (
                   <>
                     No inventory changes matched the CSV for <strong>{csvPreviewData.scopeLabel}</strong>.
                   </>
                 )}
               </p>

               {csvPreviewData.updates.length > 0 && (
                 <>
                   <h6 className="mb-2">Items to update</h6>
                 <div className="table-responsive mb-3">
                   <Table bordered striped size="sm" className="mb-0">
                     <thead>
                       <tr>
                         <th style={{ width: '48px' }} aria-label="Apply row" />
                         <th>Product</th>
                         {!csvSupermarketId && userRole === 'Admin' && <th>Supermarket</th>}
                         <th>UPC</th>
                         <th>Quantity</th>
                         <th>Price</th>
                       </tr>
                     </thead>
                     <tbody>
                       {csvPreviewData.updates.map((item) => {
                         const quantityChanged = item.currentQuantity !== item.quantity;
                         const priceChanged = item.currentPrice !== item.sale_price;
                         const rowKey = getCsvPreviewUpdateKey(item);
                         const isSelected = csvPreviewSelectedKeys.has(rowKey);
                         return (
                           <tr key={item.id} className={!isSelected ? 'text-muted' : undefined}>
                             <td className="text-center">
                               <Form.Check
                                 type="checkbox"
                                 checked={isSelected}
                                 onChange={() => toggleCsvPreviewRow(rowKey)}
                                 aria-label={`Apply update for ${item.productName}`}
                               />
                             </td>
                             <td>{item.productName}</td>
                             {!csvSupermarketId && userRole === 'Admin' && <td>{item.supermarketName}</td>}
                             <td className="font-monospace">{item.upc}</td>
                             <td>
                               {item.currentQuantity} → <strong>{item.quantity}</strong>
                               {!quantityChanged && <Badge bg="secondary" className="ms-1">unchanged</Badge>}
                             </td>
                             <td>
                               ${item.currentPrice.toFixed(2)} → <strong>${item.sale_price.toFixed(2)}</strong>
                               {!priceChanged && <Badge bg="secondary" className="ms-1">unchanged</Badge>}
                             </td>
                           </tr>
                         );
                       })}
                     </tbody>
                   </Table>
                 </div>
                 </>
               )}

               {csvPreviewData.additions?.length > 0 && (
                 <>
                   <h6 className="mb-2">Items to add</h6>
                   <div className="table-responsive mb-3">
                     <Table bordered striped size="sm" className="mb-0">
                       <thead>
                         <tr>
                           <th style={{ width: '48px' }} aria-label="Apply row" />
                           <th>Product</th>
                           <th>Supermarket</th>
                           <th>UPC</th>
                           <th>Quantity</th>
                           <th>Price</th>
                         </tr>
                       </thead>
                       <tbody>
                         {csvPreviewData.additions.map((item) => {
                           const rowKey = getCsvPreviewAdditionKey(item);
                           const isSelected = csvPreviewSelectedKeys.has(rowKey);
                           return (
                           <tr
                             key={`${item.supermarketId}-${item.productId}-${item.productUpc}`}
                             className={!isSelected ? 'text-muted' : undefined}
                           >
                             <td className="text-center">
                               <Form.Check
                                 type="checkbox"
                                 checked={isSelected}
                                 onChange={() => toggleCsvPreviewRow(rowKey)}
                                 aria-label={`Add ${item.productName} to inventory`}
                               />
                             </td>
                             <td>{item.productName}</td>
                             <td>{item.supermarketName}</td>
                             <td className="font-monospace">{item.productUpc}</td>
                             <td><strong>{item.quantity}</strong></td>
                             <td><strong>${item.sale_price.toFixed(2)}</strong></td>
                           </tr>
                           );
                         })}
                       </tbody>
                     </Table>
                   </div>
                 </>
               )}

               {csvPreviewData.pendingSupermarketUpcs?.length > 0 && (
                 <>
                   <h6 className="mb-2">Products found — select a supermarket to add</h6>
                   <ListGroup className="mb-3">
                     {csvPreviewData.pendingSupermarketUpcs.map((item) => (
                       <ListGroup.Item key={item.upc}>
                         <strong>{item.productName}</strong> (UPC: {item.upc}) — qty: {item.quantity}, price: ${item.sale_price.toFixed(2)}
                       </ListGroup.Item>
                     ))}
                   </ListGroup>
                 </>
               )}

               {csvPreviewData.notFoundUpcs?.length > 0 && (
                 <>
                   <h6 className="mb-2">UPCs not found in products</h6>
                   <ListGroup className="mb-3">
                     {csvPreviewData.notFoundUpcs.map((upc) => (
                       <ListGroup.Item key={upc} className="font-monospace">{upc}</ListGroup.Item>
                     ))}
                   </ListGroup>
                 </>
               )}

               {csvPreviewData.parseErrors?.length > 0 && (
                 <>
                   <h6 className="mb-2 text-warning">CSV row warnings</h6>
                   <ListGroup>
                     {csvPreviewData.parseErrors.map((warning, index) => (
                       <ListGroup.Item key={`${warning}-${index}`}>{warning}</ListGroup.Item>
                     ))}
                   </ListGroup>
                 </>
               )}
             </>
           )}
         </Modal.Body>
         <Modal.Footer>
           <Button variant="secondary" onClick={handleCloseCsvPreviewModal} disabled={csvUploading}>
             Cancel
           </Button>
           <Button
             variant="primary"
             onClick={handleCsvApplyConfirmed}
             disabled={(selectedCsvPreviewUpdateCount + selectedCsvPreviewAdditionCount === 0) || csvUploading}
           >
             {csvUploading ? (
               <>
                 <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                 Applying...
               </>
             ) : (
               (() => {
                 const updateCount = selectedCsvPreviewUpdateCount;
                 const addCount = selectedCsvPreviewAdditionCount;
                 if (updateCount > 0 && addCount > 0) {
                   return `Apply ${updateCount} update${updateCount === 1 ? '' : 's'} and ${addCount} addition${addCount === 1 ? '' : 's'}`;
                 }
                 if (addCount > 0) {
                   return `Add ${addCount} item${addCount === 1 ? '' : 's'}`;
                 }
                 return `Apply ${updateCount} update${updateCount === 1 ? '' : 's'}`;
               })()
             )}
           </Button>
         </Modal.Footer>
       </Modal>

       <Modal show={showCsvResultsModal} onHide={handleCloseCsvResultsModal} size="lg">
         <Modal.Header closeButton>
           <Modal.Title>CSV Upload Results</Modal.Title>
         </Modal.Header>
         <Modal.Body>
           {csvUploadResults && (
             <>
               <p className="mb-3">
                 {csvUploadResults.updatedCount > 0 && (
                   <>
                     Updated <strong>{csvUploadResults.updatedCount}</strong> inventory item
                     {csvUploadResults.updatedCount !== 1 ? 's' : ''}
                   </>
                 )}
                 {csvUploadResults.updatedCount > 0 && csvUploadResults.addedCount > 0 && ', and '}
                 {csvUploadResults.addedCount > 0 && (
                   <>
                     added <strong>{csvUploadResults.addedCount}</strong> inventory item
                     {csvUploadResults.addedCount !== 1 ? 's' : ''}
                   </>
                 )}
                 {csvUploadResults.scopeLabel ? ` in ${csvUploadResults.scopeLabel}` : ''}.
               </p>

               {csvUploadResults.addedItems?.length > 0 && (
                 <>
                   <h6 className="mb-2">Added items</h6>
                   <ListGroup className="mb-3">
                     {csvUploadResults.addedItems.slice(0, 10).map((item) => (
                       <ListGroup.Item key={item.id}>
                         <strong>{item.productName}</strong>
                         {item.supermarketName ? ` (${item.supermarketName})` : ''}
                         {' '}(UPC: {item.productUpc}) — qty: {item.quantity}, price: ${item.sale_price.toFixed(2)}
                       </ListGroup.Item>
                     ))}
                     {csvUploadResults.addedItems.length > 10 && (
                       <ListGroup.Item className="text-muted">
                         ...and {csvUploadResults.addedItems.length - 10} more
                       </ListGroup.Item>
                     )}
                   </ListGroup>
                 </>
               )}

               {csvUploadResults.updatedItems?.length > 0 && (
                 <>
                   <h6 className="mb-2">Updated items</h6>
                   <ListGroup className="mb-3">
                     {csvUploadResults.updatedItems.slice(0, 10).map((item) => (
                       <ListGroup.Item key={item.id}>
                         <strong>{item.productName}</strong>
                         {item.supermarketName ? ` (${item.supermarketName})` : ''}
                         {' '}(UPC: {item.upc}) — qty: {item.quantity}, price: ${item.sale_price.toFixed(2)}
                       </ListGroup.Item>
                     ))}
                     {csvUploadResults.updatedItems.length > 10 && (
                       <ListGroup.Item className="text-muted">
                         ...and {csvUploadResults.updatedItems.length - 10} more
                       </ListGroup.Item>
                     )}
                   </ListGroup>
                 </>
               )}

               {csvUploadResults.notFoundUpcs?.length > 0 && (
                 <>
                   <h6 className="mb-2">UPCs not found in products</h6>
                   <ListGroup className="mb-3">
                     {csvUploadResults.notFoundUpcs.map((upc) => (
                       <ListGroup.Item key={upc}>{upc}</ListGroup.Item>
                     ))}
                   </ListGroup>
                 </>
               )}

               {csvUploadResults.pendingSupermarketUpcs?.length > 0 && (
                 <>
                   <h6 className="mb-2">Products skipped — select a supermarket to add</h6>
                   <ListGroup className="mb-3">
                     {csvUploadResults.pendingSupermarketUpcs.map((item) => (
                       <ListGroup.Item key={item.upc}>
                         <strong>{item.productName}</strong> (UPC: {item.upc})
                       </ListGroup.Item>
                     ))}
                   </ListGroup>
                 </>
               )}

               {csvUploadResults.failedUpdates?.length > 0 && (
                 <>
                   <h6 className="mb-2 text-danger">Failed changes</h6>
                   <ListGroup className="mb-3">
                     {csvUploadResults.failedUpdates.map((failure) => (
                       <ListGroup.Item key={`${failure.upc}-${failure.productName}`}>
                         {failure.productName} (UPC: {failure.upc}) — {failure.message}
                       </ListGroup.Item>
                     ))}
                   </ListGroup>
                 </>
               )}

               {csvUploadResults.parseErrors?.length > 0 && (
                 <>
                   <h6 className="mb-2 text-warning">CSV row warnings</h6>
                   <ListGroup>
                     {csvUploadResults.parseErrors.map((warning, index) => (
                       <ListGroup.Item key={`${warning}-${index}`}>{warning}</ListGroup.Item>
                     ))}
                   </ListGroup>
                 </>
               )}
             </>
           )}
         </Modal.Body>
         <Modal.Footer>
           <Button variant="secondary" onClick={handleCloseCsvResultsModal}>Close</Button>
         </Modal.Footer>
       </Modal>
    </Container>
  );
};

export default InventoryPage;
